/**
 * <subscription-option>: subscribe to this flavor's 10-pack from a single-unit
 * or Dual Pack page (snippets/subscription-option.liquid).
 * Feature docs: .claude/features/feature-subscribe-on-non-10-packs/
 *
 * WHAT IT DOES
 * - Selecting the row: AOV.ai is put on Buy 1 and its highlight is hidden,
 *   the product form's variant id becomes the 10-pack's, and the hidden
 *   selling_plan input is enabled.
 * - While selected, the product's main price and the sticky bar price show
 *   the subscription price instead of AOV.ai's Buy 1 total (see PRICE DISPLAY).
 * - Clicking any AOV.ai tier: all of that is undone.
 * - While the row is selected, Add to Cart, the sticky bar button and Buy It
 *   Now send OUR request: the 10-pack, quantity 1, on the plan. Then the
 *   theme's cart drawer is refreshed and opened with its own renderContents(),
 *   or, for Buy It Now (the theme's own "skip cart" button, not Shopify's
 *   dynamic checkout button), the shopper goes straight to /checkout.
 *
 * WHY WE SEND OUR OWN CART REQUEST (verified 2026-10-09)
 * On these pages AOV.ai performs the add to cart itself
 * (/cart/add.js?app=aov-ai-bundle), even on Buy 1, using the page's own
 * product. It ignores the form's id and selling_plan fields, so swapping the
 * form alone added the single at $14.99 with no plan. We therefore catch the
 * click on window in the capture phase (before AOV.ai and the theme see it)
 * and stop it. The form is still swapped so it always describes what is
 * selected, in case anything else reads it.
 *
 * EXCLUSIVITY WITH AOV.ai (same lessons as subscription-tier-exclusive.js)
 * AOV.ai always keeps one tier selected internally, so while subscribed we
 * really select Buy 1 and only HIDE the highlight, via data-subscription-active
 * on Shopify's .shopify-app-block wrapper. subscription-tier-exclusive.css
 * already styles that attribute, so no CSS is duplicated here.
 * Our own click on Buy 1 runs with isSyncing set, so it doesn't deselect us.
 *
 * !! DEPENDS ON MARKUP WE DO NOT CONTROL !!
 * AOV.ai selectors and the theme's button classes are collected in SELECTORS.
 * If AOV.ai renames its tiers, exclusivity stops (both can look selected), but
 * our own add to cart still adds the right item. If the theme's button class
 * changes, our interception stops and AOV.ai would add the single instead,
 * so re-test add to cart after any theme or AOV.ai update.
 */
if (!customElements.get('subscription-option')) {
  const SELECTORS = {
    tier: '.Avada-Volume__Item',
    tierSelectedClass: 'Avada-Volume__Item--Selected',
    appBlock: '.shopify-app-block',
    // Main Add to Cart, sticky bar button and the optional secondary button
    // all carry this class and belong to the product form.
    addToCartButton: '.main-product-atc',
    variantIdInput: '.product-variant-id',
    // The theme's price blocks (snippets/price.liquid) that AOV.ai rewrites
    // with the selected tier's total. {section} is the section id. Each one
    // gets the subscription price while the row is selected; "long" follows
    // the store's currency-code setting, "short" never shows the code.
    priceBlocks: [
      { id: 'price-{section}', format: 'long' },
      { id: 'sticky-atc-separate-price-{section}', format: 'short' },
    ],
    priceItems: '.price__regular .price-item, .price__sale .price-item--sale',
  };

  customElements.define(
    'subscription-option',
    class SubscriptionOption extends HTMLElement {
      constructor() {
        super();
        this.isSelected = false;
        this.isSyncing = false;
        this.isAdding = false;
        this.originalVariantId = null;
        this.priceObserver = null;
        this.savedPriceTexts = new Map();

        this.onRowClick = this.onRowClick.bind(this);
        this.onRowKeydown = this.onRowKeydown.bind(this);
        this.onTierInteraction = this.onTierInteraction.bind(this);
        this.onAddToCartIntent = this.onAddToCartIntent.bind(this);
      }

      connectedCallback() {
        this.row = this.querySelector('.subscription-option__row');
        this.note = this.querySelector('.subscription-option__note');
        this.planInput = this.querySelector('input[name="selling_plan"]');
        // getAttribute, not form.id: the form's <input name="id"> shadows .id.
        this.form = document.getElementById(this.dataset.productFormId);
        if (!this.row || !this.planInput || !this.form) return;

        this.row.addEventListener('click', this.onRowClick);
        this.row.addEventListener('keydown', this.onRowKeydown);
        // Capture phase so tier choices are seen before AOV.ai's own handlers.
        // keydown too: AOV.ai selects a tier on Space/Enter WITHOUT a click.
        document.addEventListener('click', this.onTierInteraction, true);
        document.addEventListener('keydown', this.onTierInteraction, true);
        // window + capture runs before every document/element listener, which
        // is what lets us stop AOV.ai's add to cart while subscribed.
        window.addEventListener('click', this.onAddToCartIntent, true);
        window.addEventListener('submit', this.onAddToCartIntent, true);
      }

      disconnectedCallback() {
        if (this.row) {
          this.row.removeEventListener('click', this.onRowClick);
          this.row.removeEventListener('keydown', this.onRowKeydown);
        }
        document.removeEventListener('click', this.onTierInteraction, true);
        document.removeEventListener('keydown', this.onTierInteraction, true);
        window.removeEventListener('click', this.onAddToCartIntent, true);
        window.removeEventListener('submit', this.onAddToCartIntent, true);
        // Leave the form describing the one-time product, not the 10-pack.
        if (this.isSelected) this.deselect();
      }

      onRowClick() {
        if (!this.isSelected) this.select();
      }

      onRowKeydown(event) {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        if (!this.isSelected) this.select();
      }

      // A shopper picking any AOV.ai tier (click, tap, or Space/Enter on a
      // focused tier) switches the subscription off. Arrow keys only move
      // focus between tiers in AOV.ai, so they are ignored (verified 2026-10-09).
      onTierInteraction(event) {
        if (this.isSyncing || !this.isSelected) return;
        if (event.type === 'keydown' && event.key !== ' ' && event.key !== 'Enter') return;
        if (event.target.closest && event.target.closest(SELECTORS.tier)) this.deselect();
      }

      select() {
        const variantInput = this.getVariantInput();
        if (!variantInput) return;

        this.resetTierToBuyOne();
        this.originalVariantId = variantInput.value;
        variantInput.value = this.dataset.variantId;
        this.planInput.disabled = false;
        this.isSelected = true;
        this.render();
        this.startPriceOverride();
      }

      deselect() {
        this.stopPriceOverride();
        const variantInput = this.getVariantInput();
        // Only restore if the form still holds OUR 10-pack id. If the theme's
        // variant picker changed it meanwhile, its value is newer than ours.
        if (variantInput && this.originalVariantId && variantInput.value === this.dataset.variantId) {
          variantInput.value = this.originalVariantId;
        }
        this.planInput.disabled = true;
        this.isSelected = false;
        this.render();
      }

      render() {
        this.row.setAttribute('aria-checked', String(this.isSelected));
        if (this.note) this.note.hidden = !this.isSelected;

        const wrapper = this.getTierWrapper();
        if (!wrapper) return;
        if (this.isSelected) {
          wrapper.setAttribute('data-subscription-active', '');
        } else {
          wrapper.removeAttribute('data-subscription-active');
        }
      }

      getVariantInput() {
        return this.form.querySelector(SELECTORS.variantIdInput);
      }

      // PRICE DISPLAY (verified 2026-10-09)
      // AOV.ai writes the selected tier's total into the main price and the
      // sticky bar price, a moment AFTER the click (not on it). While
      // subscribed that would show the Buy 1 price ($14.99), so we show the
      // subscription price instead and re-apply it whenever AOV.ai writes.
      // We keep AOV.ai's latest text, because AOV.ai does NOT rewrite the price
      // when the shopper clicks the tier that is already selected (Buy 1):
      // we must put its text back ourselves. For any other tier, AOV.ai then
      // writes that tier's price as usual.
      getPriceBlocks() {
        const productInfo = this.closest('product-info');
        const sectionId = productInfo && productInfo.dataset.section;
        if (!sectionId) return [];
        return SELECTORS.priceBlocks
          .map((block) => ({
            element: document.getElementById(block.id.replace('{section}', sectionId)),
            text: block.format === 'long' ? this.dataset.priceLong : this.dataset.priceShort,
          }))
          .filter((block) => block.element && block.text);
      }

      // AOV.ai keeps a reference to the price's TEXT NODE and writes into it.
      // Replacing the node (e.g. with textContent =) detaches it, and every
      // later AOV.ai update is lost (verified 2026-10-09). So we only ever
      // change the existing text node's data, never the node itself.
      getPriceTextNode(item) {
        return Array.from(item.childNodes).find((node) => node.nodeType === Node.TEXT_NODE) || null;
      }

      applySubscriptionPrice() {
        this.getPriceBlocks().forEach((block, blockIndex) => {
          block.element.querySelectorAll(SELECTORS.priceItems).forEach((item, itemIndex) => {
            const textNode = this.getPriceTextNode(item);
            if (!textNode || textNode.data.trim() === block.text) return;
            this.savedPriceTexts.set(`${blockIndex}:${itemIndex}`, textNode.data);
            textNode.data = block.text;
          });
        });
      }

      startPriceOverride() {
        const blocks = this.getPriceBlocks();
        if (!blocks.length) return;
        this.savedPriceTexts.clear();
        this.applySubscriptionPrice();
        // Our own write also triggers the observer, but then the text already
        // matches, so nothing is written again (no loop).
        this.priceObserver = new MutationObserver(() => this.applySubscriptionPrice());
        blocks.forEach((block) =>
          this.priceObserver.observe(block.element, { childList: true, characterData: true, subtree: true })
        );
      }

      stopPriceOverride() {
        if (this.priceObserver) {
          this.priceObserver.disconnect();
          this.priceObserver = null;
        }
        this.getPriceBlocks().forEach((block, blockIndex) => {
          block.element.querySelectorAll(SELECTORS.priceItems).forEach((item, itemIndex) => {
            const saved = this.savedPriceTexts.get(`${blockIndex}:${itemIndex}`);
            const textNode = this.getPriceTextNode(item);
            if (saved !== undefined && textNode) textNode.data = saved;
          });
        });
        this.savedPriceTexts.clear();
      }

      // AOV.ai tiers that belong to this product (inside the same product info).
      getTiers() {
        const scope = this.closest('product-info') || document;
        return Array.from(scope.querySelectorAll(SELECTORS.tier));
      }

      getTierWrapper() {
        const firstTier = this.getTiers()[0];
        return firstTier ? firstTier.closest(SELECTORS.appBlock) : null;
      }

      resetTierToBuyOne() {
        const buyOne = this.getTiers()[0];
        if (!buyOne || buyOne.classList.contains(SELECTORS.tierSelectedClass)) return;
        this.isSyncing = true;
        try {
          buyOne.click();
        } finally {
          this.isSyncing = false;
        }
      }

      // Is this click/submit an add to cart for OUR product form?
      isAddToCartFor(event) {
        if (event.type === 'submit') return event.target === this.form;
        const button = event.target.closest && event.target.closest(SELECTORS.addToCartButton);
        return Boolean(button && button.form === this.form);
      }

      onAddToCartIntent(event) {
        if (!this.isSelected || !this.isAddToCartFor(event)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.isAdding) return;

        const button = event.type === 'submit' ? null : event.target.closest(SELECTORS.addToCartButton);
        this.addSubscriptionToCart(button, this.skipsCart(button));
      }

      // Mirrors the theme's "skip cart" buttons: the Buy It Now button
      // (data-skip-cart-button) and, if the block setting is on, the main
      // button (data-skip-cart on the variant input). The theme adds the item,
      // keeps the rest of the cart, then goes straight to checkout.
      skipsCart(button) {
        if (button && button.dataset.skipCartButton === 'true') return true;
        const variantInput = this.getVariantInput();
        const isMainButton = button && button.type === 'submit';
        return Boolean((isMainButton || !button) && variantInput && variantInput.dataset.skipCart === 'true');
      }

      async addSubscriptionToCart(button, goToCheckout) {
        this.isAdding = true;
        this.setButtonLoading(button, true);
        this.showError(false);

        const drawer = document.querySelector('cart-drawer');
        const body = {
          items: [
            {
              id: Number(this.dataset.variantId),
              quantity: 1,
              selling_plan: Number(this.dataset.sellingPlanId),
            },
          ],
        };
        if (!goToCheckout && drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(`${this.getRoot()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            this.showError(data.description || data.message || true);
            return;
          }

          if (goToCheckout) {
            window.location.href = `${this.getRoot()}checkout`;
            return;
          }

          if (drawer && typeof drawer.renderContents === 'function') {
            // An empty cart's drawer carries is-empty, which hides its
            // contents. The theme's own add to cart removes it before
            // rendering; without this the first add opens a blank drawer.
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else {
            window.location.href = this.getCartUrl();
          }
        } catch (error) {
          console.error('[subscription-option] add to cart failed', error);
          this.showError(true);
        } finally {
          this.isAdding = false;
          this.setButtonLoading(button, false);
        }
      }

      setButtonLoading(button, isLoading) {
        if (!button) return;
        button.classList.toggle('loading', isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        const spinner = button.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      // Reuse the theme's own error area on <product-form>. false hides it;
      // true shows the theme's translated generic cart error.
      showError(message) {
        const productForm = this.form.closest('product-form');
        if (!productForm || typeof productForm.handleErrorMessage !== 'function') return;
        const genericError = (window.cartStrings && window.cartStrings.error) || '';
        productForm.handleErrorMessage(message === true ? genericError : message);
      }

      getRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      }

      getCartUrl() {
        return (window.routes && window.routes.cart_url) || `${this.getRoot()}cart`;
      }
    }
  );
}
